import { Product } from "@/api/product-api";
import { CurrencyIsoCode } from "@/enums";
import { isPricedForListing } from "@/libs/product/priced-for-listing";
import { CAMO_VARIANT_BY_SLUG, camoBaseOf } from "@/libs/product/camo-variant";
import { GameResell } from "@/models/GameResell";
import { ProductUtil } from "@/utils/ProductUtil";

/**
 * 상세 페이지(PDP)의 **번들 선택 규칙** — 화면(`[slug].tsx`)과 서버(SEO·GMC 피드)가 같이 쓴다.
 *
 * 화면은 `?bundle=` 로 들어오면 그 번들을 고른 상태로 그린다. 서버 SEO(JSON-LD)와 GMC 피드는
 * **화면이 실제로 고르는 번들**을 알아야 한다 — 따로 판정하면 피드가 「FPS Bundle $730」을 내는데
 * 페이지 구조화 데이터는 「TactSuit Pro」인 불일치가 난다(#5902 실측). 그래서 규칙을 여기 한 곳에 둔다.
 */

/**
 * camo 변형 번들은 **라인으로 접는다** — 라인 대표(비-camo)가 목록에 있으면 camo 쪽은 뺀다.
 * 색상 스와치가 camo 를 고르므로 목록이 같은 선택을 두 번 묻지 않게(슬리브 PDP 사례, `[slug].tsx` 주석).
 * 대표가 목록에 없으면(카탈로그상 camo 만 잡히는 경우) 접지 않는다 — 접으면 선택이 통째로 무효가 된다.
 */
export function foldCamoBundleLines<T extends { slug: string }>(
  bundles: T[],
): T[] {
  const slugs = new Set(bundles.map((b) => b.slug));
  return bundles.filter((b) => {
    const line = camoBaseOf(b.slug);
    return line === b.slug || !slugs.has(line);
  });
}

/**
 * 이 제품에서 고를 수 있는 **업그레이드 번들 목록**(camo 라인 접기 전).
 * `useGetBundleProducts` 와 같은 사슬이다 — 구성 번들 → 판매 가능(게임 키·통화) → 가격 있음.
 *
 * ⚠️ `gameResells` 를 빈 배열로 넘기면 **게임 키 번들은 전부 빠진다**(`getValidBundles` 는 게임 키
 * 없는 번들만 무조건 통과시킨다). 서버는 게임 키 재고를 조회하지 않으므로 그렇게 쓴다 — 게임 키 번들은
 * 피드에서도 뺀다(#5858).
 */
export function getBundleUpgradeCandidates({
  products,
  product,
  gameResells,
  currency,
  countryCode,
}: {
  products: Product[];
  product: Product;
  gameResells: GameResell[];
  currency: CurrencyIsoCode;
  countryCode: string;
}): Product[] {
  return ProductUtil.getValidBundles({
    bundles: ProductUtil.getBundles(products, product),
    gameResells,
    currency,
  }).filter((b) => isPricedForListing(b, { currency, countryCode }));
}

/**
 * 선택된 번들 — 후보 slug(카드 선택 또는 `?bundle=`)를 라인으로 환산해 **목록에 있을 때만**.
 * 지역 제한으로 목록에 없으면 `null` → 기본 제품 선택이 유지된다(살 수 없는 번들을 고른 것처럼 보이지 않게).
 * `?bundle=starter-bundle-camo` 처럼 camo 변형으로 들어와도 라인으로 매칭된다.
 */
export function resolveChosenBundle({
  bundleUpgrades,
  products,
  slug,
}: {
  bundleUpgrades: { slug: string }[];
  products: Product[];
  slug: string | null | undefined;
}): Product | null {
  const line = slug ? camoBaseOf(slug) : null;
  if (!line || !bundleUpgrades.some((b) => b.slug === line)) return null;
  return products.find((p) => p.slug === line) ?? null;
}

/** 라인의 camo 변형 **제품**. 없으면 `null`. */
export function camoProductOf(
  products: Product[],
  slug: string,
): Product | null {
  return products.find((p) => p.slug === CAMO_VARIANT_BY_SLUG[slug]) ?? null;
}

/** 지금 사는 것 = 선택된 라인(번들 or 기본 제품)을 색상으로 해석한 제품. */
export function resolveDisplayProduct({
  product,
  products,
  chosenBundle,
  isCamo,
}: {
  product: Product;
  products: Product[];
  chosenBundle: Product | null;
  isCamo: boolean;
}): Product {
  const lineBase = chosenBundle ?? product;
  const lineCamo = camoProductOf(products, lineBase.slug);
  return isCamo && lineCamo ? lineCamo : lineBase;
}
